/**
 * Apreciação de risco (HRN) de uma máquina: as quatro perguntas, o resultado na hora, o bloco
 * "após adequações" e a tabela dos perigos já avaliados. O servidor calcula e grava o HRN; a
 * escala (valores e faixas) vem dele.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ErrorOutline as RiskIcon,
  CheckCircle as OkIcon,
  Refresh as ReassessIcon,
} from '@mui/icons-material';
import { DataTable, PageShell, type DataTableColumn } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import {
  machineService,
  type HrnScale,
  type Machine,
  type MachineHazardAssessment,
  type MachineHazardAssessmentFormData,
} from '../../services/machineService';
import { ConfirmDialog } from './components/MachineWizard/ConfirmDialog';
import { HazardForm, formatHrnNumber, toneOfLevel } from './components/HazardAssessment';

/** `null`: formulário fechado; `'new'`: perigo novo; senão, o perigo em edição. */
type Editing = null | 'new' | MachineHazardAssessment;

const errorMessage = (err: unknown, fallback: string): string => (err instanceof Error ? err.message : fallback);

const HrnChip: React.FC<{ hrn: number | null; level: string | null; scale: HrnScale }> = ({ hrn, level, scale }) => {
  if (hrn === null || level === null) return <Typography variant="body1">—</Typography>;
  const tone = toneOfLevel(level, scale.bands);
  return (
    <Chip
      variant="outlined"
      color={tone}
      icon={tone === 'success' ? <OkIcon /> : <RiskIcon />}
      label={`${formatHrnNumber(hrn)} · ${level}`}
    />
  );
};

const MachineHazardPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const edit = useAcao('machine.update', 'Editar apreciação');
  const canEdit = !edit.disabled;

  const [machine, setMachine] = useState<Machine | null>(null);
  const [scale, setScale] = useState<HrnScale | null>(null);
  const [hazards, setHazards] = useState<MachineHazardAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<MachineHazardAssessment | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const [loadedMachine, loadedScale, loadedHazards] = await Promise.all([
        machineService.getMachineById(id),
        machineService.getHrnScale(),
        machineService.getHazardAssessments(id),
      ]);
      setMachine(loadedMachine);
      setScale(loadedScale);
      setHazards(loadedHazards);
      setError(null);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível carregar a apreciação desta máquina.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (data: MachineHazardAssessmentFormData): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      if (editing !== null && editing !== 'new') {
        await machineService.updateHazardAssessment(id, editing.id, data);
      } else {
        await machineService.createHazardAssessment(id, data);
      }
      setEditing(null);
      await load();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível salvar o perigo.'));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (toDelete === null) return;
    const target = toDelete;
    setToDelete(null);
    try {
      await machineService.deleteHazardAssessment(id, target.id);
      await load();
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível excluir o perigo.'));
    }
  };

  const columns = useMemo((): DataTableColumn<MachineHazardAssessment>[] => {
    if (scale === null) return [];
    return [
      { id: 'hazard', label: 'Perigo', primary: true, render: (row) => row.hazard },
      {
        id: 'hrn',
        label: 'HRN hoje',
        render: (row) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <HrnChip hrn={row.hrn} level={row.hrnLevel} scale={scale} />
            {row.scaleVersion !== scale.version && (
              <Chip color="warning" icon={<ReassessIcon />} label="Reavaliar" />
            )}
          </Box>
        ),
      },
      {
        id: 'residual',
        label: 'HRN após adequações',
        render: (row) => <HrnChip hrn={row.residualHrn} level={row.residualLevel} scale={scale} />,
      },
    ];
  }, [scale]);

  const title = machine ? `Apreciação de risco · ${machine.name}` : 'Apreciação de risco';

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Máquinas', path: '/machines' },
        { label: machine?.name ?? 'Máquina' },
        { label: 'Apreciação de risco' },
      ]}
      title={title}
      subtitle="Escolha as respostas das quatro perguntas; o risco (HRN) de cada perigo é calculado na hora."
      actions={
        <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/machines')}>
          Voltar
        </Button>
      }
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress aria-label="Carregando a apreciação" />
        </Box>
      )}

      {error !== null && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!loading && scale !== null && (
        <>
          {!canEdit && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Você pode consultar os perigos desta máquina, mas não alterá-los. Para avaliar ou editar, peça a um
              administrador a permissão de editar máquinas.
            </Alert>
          )}

          {canEdit && editing !== null && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <HazardForm
                  key={editing === 'new' ? 'new' : String(editing.id)}
                  scale={scale}
                  initial={editing === 'new' ? undefined : editing}
                  saving={saving}
                  onSave={(data) => void save(data)}
                  onCancel={() => setEditing(null)}
                />
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
            <Typography variant="h6" component="h2">
              Perigos desta máquina
            </Typography>
            {canEdit && editing === null && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')} sx={{ minHeight: 44 }}>
                Avaliar um perigo
              </Button>
            )}
          </Box>

          <DataTable
            columns={columns}
            rows={hazards}
            getRowId={(row) => String(row.id)}
            emptyState={{
              title: 'Nenhum perigo avaliado nesta máquina',
              description: 'Sem a apreciação de risco, a máquina não entra no Laudo NR 12. Descreva o perigo e responda às quatro perguntas.',
            }}
            actions={[
              {
                icon: <EditIcon fontSize="small" />,
                tooltip: 'Editar',
                onClick: (row) => setEditing(row),
                hidden: () => !canEdit,
              },
              {
                icon: <DeleteIcon fontSize="small" />,
                tooltip: 'Excluir',
                color: 'error',
                onClick: (row) => setToDelete(row),
                hidden: () => !canEdit,
              },
            ]}
          />
        </>
      )}

      <ConfirmDialog
        open={toDelete !== null}
        title="Excluir este perigo?"
        message={`O perigo "${toDelete?.hazard ?? ''}" deixa de contar na apreciação desta máquina.`}
        confirmLabel="Excluir perigo"
        cancelLabel="Manter"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setToDelete(null)}
      />
    </PageShell>
  );
};

export default MachineHazardPage;
